/**
 * StageCoda.module.scss — the coda frame (board `TenthFrame.dc.html`, `.tenth` / `.tin`).
 *
 * Reads the house's `--stage-*` variables only (plan § Tokens): `--stage-accent`, `--stage-ground`,
 * `--stage-text`, `--stage-muted`, `--stage-radius`. The dashed border and the faint accent wash
 * are `core/wall/ui/Ghost.module.scss`'s recipe — the accent mixed toward transparent — at stage
 * weight (1.5px, 60%, as the board draws it), so the coda reads as the wall's ghost, not a second
 * one.
 */

.coda {
    position: relative;
    width: 100%;
    /* The inline aspect-ratio is a minimum: with overflow visible and `min-height: auto` (never set
       it to 0 here), the box's automatic minimum height is its content, so a short frame — a wide
       pack's — grows to fit the action instead of clipping it. */
    border-radius: var(--stage-radius, 16px);
    border: 1.5px dashed color-mix(in srgb, var(--stage-accent) 60%, transparent);
    background: color-mix(in srgb, var(--stage-accent) 4%, var(--stage-ground));
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
}

/* One picture, whole and faded — a grid of nine crops read as a patchwork (operator, 2026-09-25). */
.backdrop {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0.22;
    border-radius: inherit;
    pointer-events: none;
}

.body {
    position: relative;
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 22px 18px;
    border-radius: inherit;
    /* The board's scrim: the ground, denser at the centre, so the head reads over the picture. */
    background: radial-gradient(
        ellipse at 50% 45%,
        color-mix(in srgb, var(--stage-ground) 55%, transparent),
        color-mix(in srgb, var(--stage-ground) 20%, transparent) 70%
    );

    @media (min-width: 900px) {
        padding: 32px 36px;
    }
}

.pen {
    width: 22px;
    height: 22px;
    margin: 0 auto 10px;
    display: block;
    color: var(--stage-accent);

    svg {
        width: 100%;
        height: 100%;
        display: block;
    }
}

.head {
    margin: 0;
    text-align: center;
    color: var(--stage-text);
}

.lead {
    display: block;
    font-size: 13px;
    line-height: 1.4;
    letter-spacing: 0.01em;
    color: var(--stage-muted);
}

.name {
    display: block;
    font-weight: 500;
    font-size: 26px;
    line-height: 1.1;
    letter-spacing: -0.02em;
    overflow-wrap: break-word;
}

.sub {
    margin: 6px 0 16px;
    font-size: 13.5px;
    line-height: 1.45;
    text-align: center;
    color: var(--stage-muted);
}

/* The stage is wider beside the aside: the head scales with it. */
@media (min-width: 900px) {
    .lead {
        font-size: 14px;
    }

    .name {
        font-size: 32px;
    }

    .sub {
        font-size: 15px;
        margin-bottom: 20px;
    }
}

.action {
    width: 100%;
    max-width: 420px;
    margin-inline: auto;
}

/* Words going in (board frame C): the pen and the sub-line fold and the name tightens, so field,
   chips and verb sit above a phone keyboard. CSS only — no JS reads focus here.

   The fold is instant; the UNFOLD waits 250ms. A press on the verb (or a chip) blurs the field first —
   WebKit gives a pressed button no focus, so `:focus-within` drops on pointer-down — and an instant
   unfold would reflow the frame between pointer-down and pointer-up, moving the verb out from under
   the press so the click never lands (observed on toile dev, WebKit 390×844, 2026-09-23). Folding
   with animatable properties (height, margin, opacity, font-size) lets the unfold be delayed past
   the click; `display: none` could not be. */
.pen,
.sub,
.name,
.head {
    transition-property: height, margin, opacity, font-size, line-height, max-height;
    transition-duration: 0s;
    transition-delay: 250ms;
}

.sub {
    max-height: 4.5em;
    overflow: hidden;
}

/* Phones only: the fold exists to make room for a keyboard, and a desktop has none — there it would
   only move the frame under the pointer (operator, 2026-09-23). */
@media (max-width: 899px) {
    .coda:focus-within {
        .pen,
        .sub,
        .name,
        .head {
            transition-delay: 0s;
        }

        .pen {
            height: 0;
            margin-bottom: 0;
            opacity: 0;
        }

        .name {
            font-size: 20px;
        }

        .sub {
            max-height: 0;
            margin: 0;
            opacity: 0;
            font-size: 0;
            line-height: 0;
        }

        .head {
            margin-bottom: 12px;
        }
    }
}
